/**
 * Admin-migration Redirect Guard
 *
 * The admin surfaces (formerly `/admin/*` in this app) have migrated to the
 * standalone Workplace Settings app: https://github.com/ricohproducts/workplace-settings
 *
 * When `environment.SETTINGS_APP_URL` is set, any navigation into the admin
 * area opens the new app in a NEW TAB and the in-app navigation is cancelled,
 * so the user keeps their place in this app (map, booking, schedule) while the
 * Settings app opens alongside it. The trigger is always a user click (nav
 * pill, ⌘, or workspace menu), so the tab opens within transient activation
 * and is not treated as an unsolicited pop-up. If a pop-up blocker still
 * blocks the window, we fall back to a same-tab redirect so the user always
 * lands in Settings rather than nowhere.
 *
 * Behaviour is an opt-in switch keyed on the env var (`NG_APP_SETTINGS_APP_URL`):
 *   - set   → redirect to the Settings app, cancel the route (return false)
 *   - unset → allow the legacy in-app admin (return true), a no-op passthrough
 *
 * Placed FIRST in the admin route's `canActivate` so it short-circuits before
 * the auth/admin guards — an authorised user is forwarded rather than bounced.
 *
 * SECURITY NOTE: because this guard runs before `authGuard`/`adminGuard`, those
 * guards never run when the redirect is active — an unauthenticated visitor to
 * `/admin` is forwarded straight to the Settings app. That is by design; the
 * Workplace Settings app MUST enforce its own session/authorisation gate on its
 * own routes. Do not remove or weaken that gate in `workplace-settings` on the
 * assumption that this app already checked — it did not.
 *
 * The redirect target is validated to be an http(s) URL before we navigate, so
 * a misconfigured or tampered `NG_APP_SETTINGS_APP_URL` (e.g. a `javascript:`
 * or `data:` URI) can never execute in the browser (open-redirect / XSS guard).
 *
 * Deep sub-paths are intentionally NOT forwarded: the Settings app groups its
 * routes differently (e.g. `/platform/media`, `/estate/locations`) from this
 * app's flat `/admin/*` paths, so appending them would 404. Landing on the
 * Settings app root is the lower-disruption choice.
 */
import { CanActivateFn } from '@angular/router';
import { environment } from '../../../environments/environment';

export const adminRedirectGuard: CanActivateFn = () => {
  const target = environment.SETTINGS_APP_URL;
  if (target && typeof window !== 'undefined') {
    // Defensive scheme check. The target is build-time config set by ops, but
    // validate it anyway so a misconfigured or tampered value can never run a
    // javascript:/data: URI in the browser (open-redirect / XSS guard).
    // Parse as an ABSOLUTE URL (no base) — SETTINGS_APP_URL is always a full
    // origin, and omitting the base keeps document.location out of the target
    // entirely, so a relative or malformed value is rejected outright.
    let safeUrl: string | null = null;
    try {
      const parsed = new URL(target);
      if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
        safeUrl = parsed.href;
      }
    } catch {
      safeUrl = null;
    }
    if (!safeUrl) {
      // Unsafe or malformed target — serve the legacy in-app admin rather than
      // following a suspect URL.
      console.error('[adminRedirectGuard] Ignoring unsafe SETTINGS_APP_URL:', target);
      return true;
    }
    // Open the Settings app in a new tab, keeping this tab where it is.
    // NB: don't pass 'noopener' in the features string — that makes
    // window.open return null even on success, so we couldn't tell a blocked
    // pop-up apart from a successful open. Instead sever the opener handle
    // ourselves (win.opener = null) for the same security/isolation benefit.
    const win = window.open(safeUrl, '_blank');
    if (win) {
      win.opener = null;
    } else {
      // Pop-up blocked — fall back to a same-tab redirect so the user still
      // reaches Settings rather than nowhere.
      window.location.replace(safeUrl);
    }
    return false;
  }
  // Redirect disabled (env var unset) — serve the legacy in-app admin.
  return true;
};
